'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Mail, ArrowLeft, CheckCircle2, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { sendPasswordResetEmail } from '@/lib/email';

interface FormData {
  email: string;
}

interface Props {
  onBack: () => void;
}

export default function ForgotPasswordForm({ onBack }: Props) {
  const [sent, setSent] = useState(false);
  const { resetPassword } = useAuth();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>();

  const onSubmit = async (data: FormData) => {
    try {
      await resetPassword(data.email);
      try {
        const resetLink = `${process.env.NEXT_PUBLIC_SITE_URL}/auth/reset-password`;
        await sendPasswordResetEmail({ to: data.email, resetLink });
      } catch {
        // Resend failure is non-fatal
      }
      setSent(true);
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to send reset email');
    }
  };

  if (sent) {
    return (
      <div className="text-center py-4">
        <div
          className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5"
          style={{ background: 'linear-gradient(135deg, #dcfce7, #bbf7d0)' }}
        >
          <CheckCircle2 size={30} className="text-emerald-600" />
        </div>
        <h2 className="text-xl font-700 text-gray-900 mb-2">Check your inbox</h2>
        <p className="text-sm text-gray-500 mb-7 leading-relaxed">
          We&apos;ve sent a password reset link to your email address. Follow the link to set a new password.
        </p>
        <button
          onClick={onBack}
          className="w-full flex items-center justify-center gap-2 py-2.5 px-4 text-sm font-600 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
        >
          <ArrowLeft size={15} />
          Back to Sign In
        </button>
      </div>
    );
  }

  return (
    <div>
      <button
        onClick={onBack}
        className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-6 transition-colors"
      >
        <ArrowLeft size={14} />
        Back to Sign In
      </button>

      <div className="mb-7">
        <div
          className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
          style={{ background: 'linear-gradient(135deg, #dbeafe, #bfdbfe)' }}
        >
          <Mail size={20} className="text-blue-600" />
        </div>
        <h2 className="text-[1.4rem] font-700 text-gray-900 tracking-tight">Reset your password</h2>
        <p className="text-sm text-gray-500 mt-1">
          Enter your work email and we&apos;ll send you a reset link.
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label className="block text-xs font-600 text-gray-600 uppercase tracking-wide mb-1.5">
            Email Address
          </label>
          <div className="relative">
            <Mail size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="email"
              {...register('email', {
                required: 'Email address is required',
                pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
              })}
              className="w-full pl-10 pr-3.5 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all placeholder:text-gray-400"
              placeholder="you@yourcompany.co.uk"
              autoComplete="email"
            />
          </div>
          {errors.email && <p className="text-xs text-red-500 mt-1.5">{errors.email.message}</p>}
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full flex items-center justify-center gap-2 py-2.5 px-4 text-sm font-600 text-white rounded-lg transition-all duration-200 disabled:opacity-60"
          style={{
            background: isSubmitting
              ? '#6b7280' :'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
            boxShadow: isSubmitting ? 'none' : '0 2px 12px rgba(29,78,216,0.35)',
          }}
        >
          {isSubmitting ? (
            <>
              <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              Sending reset link...
            </>
          ) : (
            <>
              Send Reset Link
              <ArrowRight size={15} />
            </>
          )}
        </button>
      </form>
    </div>
  );
}
